import { CalendarX2, Copy, Pencil, Repeat, Trash2, UserPlus, UserX, Users } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ShiftPlannerConsultant } from "./shiftPlanner.types";

/** The "Repetera" presets — target dates are derived per shift. */
export type ShiftRepeatPreset = "weekdays" | "wholeWeek" | "nextWeek" | "fourWeeks";

export interface ShiftContextMenuProps {
	/** Viewport position of the right-click. */
	x: number;
	y: number;
	/** Open (unassigned) shift vs a booked pill — different action sets. */
	kind: "open" | "booked";
	/** "Ons 22/7 · 10:00 - 14:00" / "N markerade pass" — shown as the label. */
	targetLabel: string;
	/** Matching consultants for the quick Boka/Önska submenus (open kind). */
	consultants?: ShiftPlannerConsultant[];
	/** Why "Boka" is disabled for this consultant (overlap) — undefined = allowed. */
	bookDisabledReason?: (consultant: ShiftPlannerConsultant) => string | undefined;
	/** The consultant already has an open invitation on every target shift. */
	isInvited?: (consultant: ShiftPlannerConsultant) => boolean;
	/** "Välj konsult…" / "Byt konsult…" — open the floating consultant panel. */
	onPickConsultant?: () => void;
	onBook?: (consultant: ShiftPlannerConsultant) => void;
	onInvite?: (consultant: ShiftPlannerConsultant) => void;
	/** "Ta bort konsult (behåll passet)" — booked kind only. */
	onUnassign?: () => void;
	/** "Registrera frånvaro" — booked kind only, opens the absence flow for
	 * the shift's consultant. */
	onReportAbsence?: () => void;
	/** "Ändra tider" — open the edit popover for the targets. */
	onChangeTimes: () => void;
	/** "Duplicera" — one unassigned copy of every target, same day and times. */
	onDuplicate: () => void;
	/** "Repetera" — copies of every target on the preset's dates. */
	onRepeat: (preset: ShiftRepeatPreset) => void;
	/** "Ta bort" — delete the targets. */
	onRemove: () => void;
	onClose: () => void;
}

/**
 * The consultant list of one Boka/Önska submenu, with its own search box —
 * the matching list can be long, and finding a specific person by scrolling
 * a hover-menu is painful. Filters on name or employee number. Own component
 * so each submenu keeps its own query (and it resets when the menu closes).
 */
const ConsultantSubmenuList: React.FC<{
	consultants: ShiftPlannerConsultant[];
	/** Why the row is disabled — undefined = pickable (book submenu only). */
	disabledReason?: (consultant: ShiftPlannerConsultant) => string | undefined;
	/** The consultant is already invited — disabled row with an "Önskad" tag
	 * (invite submenu only). */
	isInvited?: (consultant: ShiftPlannerConsultant) => boolean;
	onPick: (consultant: ShiftPlannerConsultant) => void;
}> = ({ consultants, disabledReason, isInvited, onPick }) => {
	const { t } = useTranslation();
	const [query, setQuery] = useState("");

	const trimmed = query.trim().toLowerCase();
	const filtered = trimmed
		? consultants.filter(
				(consultant) =>
					consultant.name.toLowerCase().includes(trimmed) ||
					String(consultant.employeeNumber ?? "").includes(trimmed)
		  )
		: consultants;

	return (
		<>
			<div className="border-b border-gray-100 p-1.5">
				<input
					type="text"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder={t("shiftPlanner.searchConsultants") as string}
					// Radix's menu typeahead listens on keydown and would steal
					// letters (jump focus to items) — keep typing inside the field
					onKeyDown={(e) => e.stopPropagation()}
					className="w-full rounded border border-solid border-gray-300 px-2 py-1 text-sm outline-none focus:border-brand"
				/>
			</div>
			<div className="max-h-64 overflow-y-auto p-1">
				{filtered.length === 0 ? (
					<DropdownMenuItem disabled>{t("shiftPlanner.noMatchingConsultants")}</DropdownMenuItem>
				) : (
					filtered.map((consultant) => {
						const blockReason = disabledReason?.(consultant);
						const invited = isInvited?.(consultant) ?? false;
						return (
							<DropdownMenuItem
								key={consultant.id}
								disabled={!!blockReason || invited}
								title={blockReason ?? (invited ? (t("shiftPlanner.invited") as string) : undefined)}
								onSelect={() => onPick(consultant)}
							>
								<span className="min-w-0 flex-1 truncate">{consultant.name}</span>
								{consultant.employeeNumber != null && (
									<span className="ml-2 text-[11px] tabular-nums text-gray-400">
										#{consultant.employeeNumber}
									</span>
								)}
								{invited && (
									<span className="ml-2 text-[11px] text-orange-500">
										{t("shiftPlanner.invited")}
									</span>
								)}
							</DropdownMenuItem>
						);
					})
				)}
			</div>
		</>
	);
};

/**
 * The right-click menu of a shift pill, built on the shadcn dropdown-menu
 * (controlled open + an invisible trigger pinned at the pointer — same look
 * and behavior as a context menu, no extra dependency).
 *
 * OPEN shifts: pick a consultant in the floating panel, quick-book or
 * quick-invite from searchable submenus, change times, duplicate, repeat
 * across days, delete. BOOKED pills: change or unassign the consultant,
 * change times, duplicate (as an unassigned copy), repeat (as booked
 * copies), delete.
 */
const ShiftContextMenu: React.FC<ShiftContextMenuProps> = ({
	x,
	y,
	kind,
	targetLabel,
	consultants = [],
	bookDisabledReason,
	isInvited,
	onPickConsultant,
	onBook,
	onInvite,
	onUnassign,
	onReportAbsence,
	onChangeTimes,
	onDuplicate,
	onRepeat,
	onRemove,
	onClose,
}) => {
	const { t } = useTranslation();

	return (
		<DropdownMenu open onOpenChange={(open) => !open && onClose()}>
			<DropdownMenuTrigger asChild>
				{/* Invisible anchor pinned at the pointer — the menu opens here */}
				<span aria-hidden style={{ position: "fixed", left: x, top: y, width: 1, height: 1 }} />
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="start"
				sideOffset={2}
				className="w-64"
				onContextMenu={(e) => e.preventDefault()}
				// Clicks bubble through the React tree into the hosting cell —
				// same guard as every popover hosted inside the planner grid
				onClick={(e) => e.stopPropagation()}
			>
				<DropdownMenuLabel className="font-normal">
					<span className="block text-xs text-gray-500">{targetLabel}</span>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />

				{kind === "open" && onPickConsultant && (
					<DropdownMenuItem onSelect={onPickConsultant}>
						<Users className="mr-2 h-4 w-4" />
						{t("shiftPlanner.matchConsultant")}…
					</DropdownMenuItem>
				)}
				{kind === "open" && onBook && (
					<DropdownMenuSub>
						<DropdownMenuSubTrigger>
							<UserPlus className="mr-2 h-4 w-4" />
							{t("shiftPlanner.menuBook")}
						</DropdownMenuSubTrigger>
						<DropdownMenuSubContent className="w-60 p-0">
							<ConsultantSubmenuList
								consultants={consultants}
								disabledReason={bookDisabledReason}
								onPick={onBook}
							/>
						</DropdownMenuSubContent>
					</DropdownMenuSub>
				)}
				{kind === "open" && onInvite && (
					<DropdownMenuSub>
						<DropdownMenuSubTrigger>
							<UserPlus className="mr-2 h-4 w-4 text-orange-500" />
							{t("shiftPlanner.menuInvite")}
						</DropdownMenuSubTrigger>
						<DropdownMenuSubContent className="w-60 p-0">
							<ConsultantSubmenuList
								consultants={consultants}
								isInvited={isInvited}
								onPick={onInvite}
							/>
						</DropdownMenuSubContent>
					</DropdownMenuSub>
				)}

				{kind === "booked" && onPickConsultant && (
					<DropdownMenuItem onSelect={onPickConsultant}>
						<Users className="mr-2 h-4 w-4" />
						{t("shiftPlanner.changeConsultant")}…
					</DropdownMenuItem>
				)}
				{kind === "booked" && onUnassign && (
					<DropdownMenuItem onSelect={onUnassign}>
						<UserX className="mr-2 h-4 w-4" />
						{t("shiftPlanner.unassignConsultant")}
					</DropdownMenuItem>
				)}
				{kind === "booked" && onReportAbsence && (
					<DropdownMenuItem onSelect={onReportAbsence}>
						<CalendarX2 className="mr-2 h-4 w-4" />
						{t("REGISTER_ABSENCE")}
					</DropdownMenuItem>
				)}

				<DropdownMenuSeparator />
				<DropdownMenuItem onSelect={onChangeTimes}>
					<Pencil className="mr-2 h-4 w-4" />
					{t("shiftPlanner.changeTimes")}
				</DropdownMenuItem>
				<DropdownMenuItem onSelect={onDuplicate}>
					<Copy className="mr-2 h-4 w-4" />
					{t("shiftPlanner.duplicate")}
				</DropdownMenuItem>
				<DropdownMenuSub>
					<DropdownMenuSubTrigger>
						<Repeat className="mr-2 h-4 w-4" />
						{t("shiftPlanner.repeat")}
					</DropdownMenuSubTrigger>
					<DropdownMenuSubContent className="w-64">
						{(
							[
								["weekdays", t("shiftPlanner.repeatWeekdays")],
								["wholeWeek", t("shiftPlanner.repeatWholeWeek")],
								["nextWeek", t("shiftPlanner.repeatNextWeek")],
								["fourWeeks", t("shiftPlanner.repeatFourWeeks")],
							] as [ShiftRepeatPreset, string][]
						).map(([preset, label]) => (
							<DropdownMenuItem key={preset} onSelect={() => onRepeat(preset)}>
								{label}
							</DropdownMenuItem>
						))}
					</DropdownMenuSubContent>
				</DropdownMenuSub>

				<DropdownMenuSeparator />
				<DropdownMenuItem
					onSelect={onRemove}
					className="text-danger focus:bg-danger-muted focus:text-danger"
				>
					<Trash2 className="mr-2 h-4 w-4" />
					{t("shiftPlanner.removeShift")}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default ShiftContextMenu;
